import { Stripe, StripeCardElement } from '@stripe/stripe-js'
import BillingMethod from 'billing/models/BillingMethod'
import { CardPaymentMethodEditorPorts } from 'billing/payment-methods/ports'
import { PaymentRequired } from 'fejl'
import Member from 'iam/models/Member'
import { Err, Ok, Result } from 'infra/result'
import { action, computed, intercept, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import { Id } from 'types/definitions'
import { extractMessageFromError } from 'utils/error-util'
import { required, validationContext } from 'validx'
import CheckoutViewModel from '../../CheckoutViewModel'

export interface CardPaymentFormData {
  /**
   * Stripe instance.
   */
  stripe: Stripe

  /**
   * Full name on the card.
   * Default to the current member's display name.
   */
  fullName: string

  /**
   * Address.
   */
  address: string

  /**
   * Address city.
   */
  city: string
  /**
   * Country code.
   */
  country: string

  /**
   * Zip code.
   */
  zipCode: string

  /**
   * The Stripe card element, needed to create a token.
   */
  card: StripeCardElement
}

export default class CardPaymentMethodFormViewModel {
  /**
   * Full name on the card.
   * Default to the current member's display name.
   */
  @observable fullName = this.member.displayName

  /**
   * Country code.
   */
  @observable country = 'US'

  /**
   * Zip code.
   */
  @observable zipCode = ''

  /**
   * The Stripe card element, needed to create a token.
   */
  card: StripeCardElement | null = null

  /**
   * Validation.
   */
  validation = validationContext(this, {
    fullName: [required('Name is required')],
    country: [required('Country is required')],
    zipCode: [required('Zip code is required')],
  })

  constructor(
    private member: Member,
    private onSubmit: () => Promise<void>,
    private cardPaymentMethodEditorAdapter: CardPaymentMethodEditorPorts,
    private stripe: Stripe | null,
    private checkoutVM: CheckoutViewModel,
  ) {
    makeObservable(this)
    // Clears validation on field changes.
    intercept(this, (change) => {
      this.validation.clearErrors(String(change.name))
      return change
    })
  }

  /**
   * Wether an existing payment method is selected
   */
  @computed
  get formDisabled() {
    return Boolean(this.checkoutVM.selectedPaymentMethod) || this.pay.pending
  }

  /**
   * Submits the job with the selected payment method
   */
  pay = task.resolved(async () => {
    return await this.onSubmit()
  })

  /**
   * Sets the country.
   */
  @action.bound
  setCountry(value: string) {
    this.country = value
  }

  /**
   * Listen, I don't need your judgement right now.
   *
   * Makeshift way of figuring out what the server error pertains to,
   * and assign the error to the right field in the validation context.
   *
   * @param err
   * @private
   */
  @action.bound
  private maybeSniffMeaningfulInfoFromError(err: unknown) {
    const message = extractMessageFromError(err)
    if (/zip code/i.test(message)) {
      this.validation.addErrors({ zipCode: [message] })
      return true
    }

    if (/card/i.test(message) || err instanceof PaymentRequired) {
      this.validation.addErrors({ card: [message] })
      return true
    }

    return false
  }

  /**
   * Creates a payment method with the given card info.
   * @param alert
   * @returns
   */
  @action
  async savePaymentMethod(): Promise<Result<BillingMethod, string>> {
    if (!this.validation.validate().isValid) {
      return Err(
        'You must select an existing payment method or fill the form to add a new one.',
      )
    }

    if (!this.stripe || !this.card) {
      return Err('Stripe was not loaded.')
    }

    const result = await this.stripe.createToken(this.card, {
      name: this.fullName,
      address_country: this.country,
      address_zip: this.zipCode,
    })

    if (result.error) {
      this.validation.addErrors({
        card: [result.error.message || 'There was a problem with your card.'],
      })
      return Err('There was a problem with your card.')
    }

    try {
      const billingMethod =
        await this.cardPaymentMethodEditorAdapter.createBillingMethod(
          this.member,
          null,
          Id.assert(result.token?.id),
        )

      return Ok(billingMethod)
    } catch (err) {
      if (this.maybeSniffMeaningfulInfoFromError(err)) {
        return Err(extractMessageFromError(err))
      }
      throw err
    }
  }
}
